import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState({ company_name: "", logo_url: "", working_hours_start: "", working_hours_end: "" });
  const [gates, setGates] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [newGate, setNewGate] = useState("");
  const [newPurpose, setNewPurpose] = useState("");
  const [badges, setBadges] = useState([]);
  const [selectedGateId, setSelectedGateId] = useState("");
  const [newBadgeNumber, setNewBadgeNumber] = useState("");
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const load = async () => {
    const [s, g, p] = await Promise.all([api.get("/settings"), api.get("/gates"), api.get("/purposes")]);
    setSettings(s.data);
    setGates(g.data);
    setPurposes(p.data);
    if (!selectedGateId && g.data.length) setSelectedGateId(g.data[0].id);
  };

  const loadBadges = async (gateId) => {
    if (!gateId) {
      setBadges([]);
      return;
    }
    const { data } = await api.get(`/badges?gate_id=${gateId}`);
    setBadges(data);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadBadges(selectedGateId);
  }, [selectedGateId]);

  const saveSettings = async () => {
    await api.put("/settings", settings);
    toast.success("Settings saved");
  };

  const addGate = async () => {
    if (!newGate) return;
    await api.post("/gates", { name: newGate });
    setNewGate("");
    load();
  };

  const addPurpose = async () => {
    if (!newPurpose) return;
    await api.post("/purposes", { name: newPurpose });
    setNewPurpose("");
    load();
  };

  const removeGate = async (id) => {
    await api.delete(`/gates/${id}`);
    toast.success("Gate removed");
    load();
  };

  const removePurpose = async (id) => {
    await api.delete(`/purposes/${id}`);
    toast.success("Purpose removed");
    load();
  };

  const addBadge = async () => {
    if (!newBadgeNumber || !selectedGateId) return;
    try {
      await api.post("/badges", { badge_number: newBadgeNumber, gate_id: selectedGateId });
      setNewBadgeNumber("");
      loadBadges(selectedGateId);
      toast.success("Badge added to inventory");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeBadge = async (id, status) => {
    await api.delete(`/badges/${id}`);
    toast.success(status === "issued" ? "Badge removed — it was currently with an active visitor" : "Badge removed");
    loadBadges(selectedGateId);
  };

  const runFactoryReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/factory-reset");
      toast.success(
        `Factory reset complete — ${data.visits_deleted} visits, ${data.invites_deleted} invites, ${data.audit_logs_deleted} audit logs, ${data.non_admin_users_deleted} non-admin users deleted, ${data.badges_reset} badges reset to available.`,
        { duration: 8000 }
      );
      setResetDialogOpen(false);
      setResetConfirmText("");
      loadBadges(selectedGateId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="settings-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Configuration</span>
        <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Settings</h1>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-4 max-w-lg">
        <h3 className="text-lg font-bold uppercase text-brand-navy">Branding &amp; Hours</h3>
        <Input
          data-testid="settings-company-name"
          placeholder="Company name"
          value={settings.company_name}
          onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
          className="rounded-sm bg-white border-slate-300"
        />
        <Input
          data-testid="settings-logo-url"
          placeholder="Logo URL"
          value={settings.logo_url || ""}
          onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
          className="rounded-sm bg-white border-slate-300"
        />
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="settings-hours-start"
            type="time"
            value={settings.working_hours_start}
            onChange={(e) => setSettings({ ...settings, working_hours_start: e.target.value })}
            className="rounded-sm bg-white border-slate-300 w-full"
          />
          <Input
            data-testid="settings-hours-end"
            type="time"
            value={settings.working_hours_end}
            onChange={(e) => setSettings({ ...settings, working_hours_end: e.target.value })}
            className="rounded-sm bg-white border-slate-300 w-full"
          />
        </div>
        <Button onClick={saveSettings} data-testid="settings-save-button" className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
          Save
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-slate-200 bg-white p-6 space-y-3">
          <h3 className="text-lg font-bold uppercase text-brand-navy">Gates / Locations</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="new-gate-input"
              placeholder="Gate name"
              value={newGate}
              onChange={(e) => setNewGate(e.target.value)}
              className="rounded-sm bg-white border-slate-300 w-full"
            />
            <Button onClick={addGate} data-testid="add-gate-button" className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light w-full sm:w-auto shrink-0">
              Add
            </Button>
          </div>
          {gates.map((g) => (
            <div key={g.id} data-testid={`gate-item-${g.id}`} className="flex justify-between items-center border-b border-slate-100 py-2 text-sm text-slate-700">
              <span>{g.name}</span>
              <button onClick={() => removeGate(g.id)} data-testid={`remove-gate-${g.id}`} className="text-slate-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="border border-slate-200 bg-white p-6 space-y-3">
          <h3 className="text-lg font-bold uppercase text-brand-navy">Visit Purposes</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="new-purpose-input"
              placeholder="Purpose name"
              value={newPurpose}
              onChange={(e) => setNewPurpose(e.target.value)}
              className="rounded-sm bg-white border-slate-300 w-full"
            />
            <Button onClick={addPurpose} data-testid="add-purpose-button" className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light w-full sm:w-auto shrink-0">
              Add
            </Button>
          </div>
          {purposes.map((p) => (
            <div key={p.id} data-testid={`purpose-item-${p.id}`} className="flex justify-between items-center border-b border-slate-100 py-2 text-sm text-slate-700">
              <span>{p.name}</span>
              <button onClick={() => removePurpose(p.id)} data-testid={`remove-purpose-${p.id}`} className="text-slate-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-4" data-testid="badge-inventory-card">
        <div>
          <h3 className="text-lg font-bold uppercase text-brand-navy">Badge Inventory</h3>
          <p className="text-sm text-slate-500">Physical badge numbers available for each gate. Issued badges return to the pool at checkout.</p>
        </div>
        <Select value={selectedGateId} onValueChange={setSelectedGateId}>
          <SelectTrigger data-testid="badge-gate-select" className="rounded-sm bg-white border-slate-300 max-w-xs">
            <SelectValue placeholder="Select a gate" />
          </SelectTrigger>
          <SelectContent>
            {gates.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-col sm:flex-row gap-2 max-w-md">
          <Input
            data-testid="new-badge-input"
            placeholder="Badge number"
            type="tel"
            inputMode="numeric"
            value={newBadgeNumber}
            onChange={(e) => setNewBadgeNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBadge())}
            disabled={!selectedGateId}
            className="rounded-sm bg-white border-slate-300 w-full"
          />
          <Button
            onClick={addBadge}
            data-testid="add-badge-button"
            disabled={!selectedGateId || !newBadgeNumber}
            className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light w-full sm:w-auto shrink-0"
          >
            Add
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="badge-list">
          {badges.map((b) => (
            <div
              key={b.id}
              data-testid={`badge-item-${b.id}`}
              className={`flex items-center justify-between border px-3 py-2 ${b.status === "issued" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}
            >
              <div>
                <p className="font-mono font-bold text-brand-navy">{b.badge_number}</p>
                <p className={`text-xs uppercase font-bold ${b.status === "issued" ? "text-amber-600" : "text-emerald-600"}`}>{b.status}</p>
              </div>
              <button onClick={() => removeBadge(b.id, b.status)} data-testid={`remove-badge-${b.id}`} className="text-slate-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {selectedGateId && !badges.length && <p className="text-sm text-slate-400 col-span-full">No badges added yet for this gate</p>}
        </div>
      </div>

      <div className="border-2 border-red-200 bg-red-50 p-6 space-y-4" data-testid="danger-zone-card">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={22} />
          <div>
            <h3 className="text-lg font-bold uppercase text-red-700">Danger Zone</h3>
            <p className="text-sm text-red-600">
              Factory Reset permanently deletes all Visitor Logs, Invites, and Audit Logs, and removes every non-admin
              User (Employee/Security). Badges are reset to "available". Gates, Purposes, Briefings, and Settings are
              kept. This action cannot be undone.
            </p>
          </div>
        </div>
        <AlertDialog open={resetDialogOpen} onOpenChange={(v) => (!resetting && (setResetDialogOpen(v), !v && setResetConfirmText("")))}>
          <AlertDialogTrigger asChild>
            <Button data-testid="factory-reset-trigger-button" className="rounded-sm bg-red-600 text-white hover:bg-red-700 font-bold uppercase text-sm">
              Factory Reset
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="factory-reset-dialog" className="rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="uppercase text-red-700">Confirm Factory Reset</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all Visitor Logs, Invites, and Audit Logs, and remove every non-admin
                User. Type <strong>RESET</strong> below to confirm — this cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              data-testid="factory-reset-confirm-input"
              placeholder='Type "RESET" to confirm'
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              className="rounded-sm bg-white border-red-300"
              autoFocus
            />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="factory-reset-cancel-button">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="factory-reset-confirm-button"
                disabled={resetConfirmText !== "RESET" || resetting}
                onClick={(e) => {
                  e.preventDefault();
                  runFactoryReset();
                }}
                className="rounded-sm bg-red-600 text-white hover:bg-red-700"
              >
                {resetting ? "Resetting..." : "Yes, Wipe Everything"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
